//This is using jquery-validation plug in
//https://jqueryvalidation.org/

// @ts-check
//#region Typedefs

/**
 * @typedef {Object} CodeAddress
 * @property {string|null} addressLine1
 * @property {string|null} addressLine2
 * @property {string|null} city
 * @property {string|null} stateProvince
 * @property {string|null} county
 * @property {string|null} postalCode
 * @property {string|null} countryName
 * @property {string|null} country
 * @property {string|null} plus4
 * @property {string|null} latitude
 * @property {string|null} longitude
 * @property {string|null} melissaAddressKey
 * @property {string|null} addressTypeCode
 * @property {string|null} [resultCode]
 * @property {string|null} [resultCodes]
 * @property {MelissaDataResult[]} melissaDataResults
 */

/**
 * @typedef {Object} MelissaAddressVerificationResponse
 * @property {number} AddressValid
 * @property {boolean} IsUnitedStatesAddress
 * @property {CodeAddress[]} CodedAddresses
 */

/**
 * @typedef {MelissaAddressVerificationResponse} AddressVerificationResponse
 */

/**
 * @typedef {Object} MelissaVerificationAction
 * @property {boolean} hasErrors
 * @property {boolean} hasRecommendations
 * @property {boolean} requiresUserConfirmation
 * @property {string} error
 * @property {CodeAddress|null} recommendedAddress
 */

/**
 * @typedef {Object} MelissaDataResult
 * @property {string} resultCode
 * @property {string} description
 */

/**
 * @typedef {Object} AddressVerificationRequest
 * @property {string} addressLine1
 * @property {string|null} addressLine2
 * @property {string|null} city
 * @property {string|null} stateProvince
 * @property {string|null} county
 * @property {string} postalCode
 */

//#endregion
$(function () {
    //#region Constants

    const $form = $("#studentRegistrationForm");
    const $secondaryPhoneNumber = $("#SecondaryPhoneNumber");
    const $primaryAddressContainer = $(".primary-addres-container");
    const $primaryLatitude = $primaryAddressContainer.find(".latitude");
    const $primaryLongitude = $primaryAddressContainer.find(".longitude");
    const $primaryMelissaAddressKey = $primaryAddressContainer.find(".melissa-address-key");
    const $allFormInputs = $form.find("input,select,textarea");
    const $primaryAddressValidatedOverride = $("#PrimaryAddressValidationOverride");
    const $firstName = $("#FirstName");
    const $intendedUses = $("[name='IntendedUse']");
    const $loadingIndicator = $('#loading-indicator');
    const $submitBtn = $('#AgreeSubmitBtn');

    const addressVerifyUrl = window.location.origin + "/customers/address-verify";
    const addressAutoCompleteUrl = window.location.origin + "/customers/address-autocomplete";

    const verifyPrimaryAddressModalTitle = "Verify Home Shipping Address";

    // Used for storing error messages from the server. Are cleared out on the
    // corresponding inputs change event.
    const serverValidationMessages = {};

    // Indicates if the beforeunoad event was already added.
    let addedBeforeUnloadEventHandler = false;

    // Track if the address have been validated.
    let primaryAddressValidated = false;

    //#endregion

    //#region Address Validation

    /**
     * Checks if two addresses are functionally identical.
     * @param {CodeAddress} addressA 
     * @param {CodeAddress} addressB 
     */
    function areAddressesSame(addressA, addressB) {
        if (addressA === null || addressA === undefined) {
            throw new Error("The argument \"addressA\" cannot be null/undefined.");
        } else if (addressB === null || addressB === undefined) {
            throw new Error("The argument \"addressB\" cannot be null/undefined.");
        }

        return addressA.addressLine1 === addressB.addressLine1
            && addressA.addressLine2 === addressB.addressLine2
            && addressA.city == addressB.city
            && addressA.county === addressB.county
            && addressA.postalCode === addressB.postalCode
            && addressA.stateProvince === addressB.stateProvince;
    }

    /**
     * Checks if the address has any error messages.
     * @param {MelissaDataResult} [melissaDataResult] 
     * @returns {boolean} Returns true if the address is not considered valid
     * by Melissa.
     */
    function doesMelissaDataResultHaveError(melissaDataResult) {
        return (melissaDataResult?.resultCode.startsWith("GE") ?? false)
            || (melissaDataResult?.resultCode.startsWith("AE") ?? false);
    }

    /**
     * Event handler for the primary address.
     * @returns {void}
     */
    function validatePrimaryAddress() {
        // Mark the address as invalid.
        primaryAddressValidated = false;
        $primaryMelissaAddressKey.val("");
        $primaryLatitude.val("");
        $primaryLongitude.val("");

        // Check for any invalid elements in the address.
        let hasInvalidInputs = false;

        $primaryAddressContainer.find("input,select").each(function (el) {
            const $this = $(this);
            const inputName = $this.attr("name") ?? "";
            const rules = $form.data("validator").settings.rules[inputName] ?? {};
            const validationRule = "required" in rules ? rules.required : false;
            const validationRuleType = typeof (validationRule);
            let isInputRequired = false;

            if (validationRuleType === "function") {
                isInputRequired = validationRule(this);
            } else if (validationRuleType === "boolean") {
                isInputRequired = validationRule;
            }

            const isInputDirty = $this.attr("data-dirty") === "true";

            // If the input has not yet been touched by the user and is
            // required then do not yet perform validation.
            if (!isInputDirty && isInputRequired) {
                hasInvalidInputs = true;
                return;
            }

            // @ts-ignore
            if (!$(this).valid()) {
                hasInvalidInputs = true;
            }
        });

        if (hasInvalidInputs) {
            return;
        }

        const address = getAddressFromForm($primaryAddressContainer);
        $primaryAddressValidatedOverride.prop("checked", false);

        // Block the UI and show suggestions (if any).
        blockUI(true);

        showValidationModalForAddressIfNeededAsync(
            verifyPrimaryAddressModalTitle,
            address,
            newAddress => setAddressFormSection($primaryAddressContainer, newAddress),
            () => $primaryAddressValidatedOverride.prop("checked", true),
            codedAddress => {
                setAddressFormSection($primaryAddressContainer, codedAddress);
                $primaryAddressValidatedOverride.prop("checked", false);
            }
        )
            .then(() => primaryAddressValidated = true)
            .catch(error => {
                primaryAddressValidated = false;
                $primaryMelissaAddressKey.val("");
                $primaryLatitude.val("");
                $primaryLongitude.val("");

                if (error !== "dismissed") {
                    console.error(error);
                }
            })
            .finally(() => blockUI(false));
    }

    /**
     * Checks if the argument 'data' matches the AddressVerification interface.
     * @param {any} data 
     * @returns {data is MelissaAddressVerificationResponse}
     */
    function isMelissaAddressVerificationResponse (data) {
        if (data === null || data === undefined) {
            return false;
        }

        const result = "IsUnitedStatesAddress" in data
            && "AddressValid" in data
            && "CodedAddresses" in data
            // && (data.CodedAddresses.length === 0 || data.CodedAddresses.every(x => isCodedAddress(x)));
            ;

        return result;
    }

    /**
     * Determines what the next step should be based on a
     * AddressVerificationResponse.
     * @param {AddressVerificationResponse} verificationResponse 
     * @param {CodeAddress} currentAddress
     * @returns {MelissaVerificationAction}
     */
    function determineMelissaVerificationActionV2 (verificationResponse, currentAddress) {
        /** @type {MelissaVerificationAction} */
        const response = {
            hasErrors: false,
            hasRecommendations: false,
            requiresUserConfirmation: false,
            error: "",
            recommendedAddress: null,
        };

        if (isMelissaAddressVerificationResponse(verificationResponse)) {
            // Address was validated but detected some issues.
            let hasInvalidCodes = verificationResponse.CodedAddresses.some(
                x => x.melissaDataResults?.some(
                    doesMelissaDataResultHaveError
                ) ?? false
            );

            // If there is an error, display set the error message.
            if (
                hasInvalidCodes
                && (verificationResponse['message'] === null || verificationResponse['message'] === undefined)
            ) {
                // Need to handle AE09 differently. When those occur, should
                // show the message the folloing message:
                // "The street address was verified but the suite/apartment number is missing or invalid."
                const customErrorMessages = {
                    AE09: "The street address was verified but the suite/apartment number is missing or invalid.",
                    AE08: "The street address was verified but the suite/apartment number is missing or invalid.",
                };

                if (verificationResponse.CodedAddresses === null || verificationResponse.CodedAddresses === undefined) {
                    throw new Error("The property \"CodedAddresses\" cannot be null/undefined.");
                }

                // @ts-ignore
                const combinedErrorMessage = verificationResponse.CodedAddresses
                    // @ts-ignore
                    .filter(x => x.melissaDataResults.some(doesMelissaDataResultHaveError))
                    .map(x => x.melissaDataResults)
                    // @ts-ignore
                    .reduce((previousValue, currentValue) => previousValue.concat(currentValue), [])
                    .filter(doesMelissaDataResultHaveError)
                    .map(x => {
                        if (x.resultCode in customErrorMessages) {
                            return customErrorMessages[x.resultCode];
                        } else {
                            return x.description;
                        }
                    })
                    .join("<br />");

                verificationResponse['message'] = combinedErrorMessage;
            }

            /**
             * Checks if a CodeAddress is valid.
             * @param {CodeAddress} x
             * @returns {boolean}
             */
            const isValidAddress = x => x.addressLine1 !== null
                && x.stateProvince !== null
                && x.city !== null
                && x.postalCode !== null
                && x.countryName !== null
                // && !x.melissaDataResults.some(y => y.resultCode.startsWith("AE")) // I guess we need to allow Address Errors (AE) now...
                // @ts-ignore
                && !x.melissaDataResults.some(y => y.resultCode.startsWith("GE"))
                && (
                    (x.melissaDataResults?.some(y => y.resultCode === "AS01") ?? false)
                    || (x.melissaDataResults?.some(y => y.resultCode === "AS02") ?? false)
                    || (x.melissaDataResults?.some(y => y.resultCode === "AS03") ?? false)
                    || (x.melissaDataResults?.some(y => y.resultCode === "GS01") ?? false)
                );

            // Determine if should error or show address suggestions.
            /** @type {CodeAddress[]} */
            const validCodedAddress = verificationResponse.CodedAddresses?.filter(isValidAddress) ?? [];

            // The following result codes can be auto-applied without
            // user confirmation(however all other ACXX codes require
            // user confirmation):
            const changesThatCanBeAutoApplied = [
                "AC09",
                "AC11",
                "AC13",
                "AC14",
                "AC15",
                "AC16",
                "AC17",
                "AC19",
                "AC22",
            ];

            const hasRecommendedAddressesThatRequireUserConfirmation = validCodedAddress.some(
                // @ts-ignore
                x => x.melissaDataResults
                    .filter(y => y.resultCode.startsWith("AC"))
                    .some(y => y.resultCode.startsWith("AC") && !changesThatCanBeAutoApplied.includes(y.resultCode))
            );

            response.requiresUserConfirmation = hasRecommendedAddressesThatRequireUserConfirmation;

            if (hasInvalidCodes) {
                response.hasErrors = true;
                response.error = verificationResponse['message'];
            }

            if (validCodedAddress.length > 0) {
                response.recommendedAddress = currentAddress;
            } else {
                response.error = "Unable to validate address.";
                response.hasErrors = true;
                response.hasRecommendations = false;
                response.recommendedAddress = null;
                response.requiresUserConfirmation = true;
            }
        } else {
            throw new Error("Unknown response, not implemented");
        }

        return response;
    }

    /**
     * Shows a modal for the recommended addresses.
     * @param {string} title
     * @param {CodeAddress[]} recommendedAddresses 
     * @param {CodeAddress} enteredAddress
     * @param {(arg0: CodeAddress) => void} onSelectedAddressFn
     * @param {(arg0: CodeAddress) => void} onSelectedUserEnteredAddressFn
     * @returns {Promise}
     */
    function showAddressRecommendationsAsync(
        title,
        recommendedAddresses,
        enteredAddress,
        onSelectedAddressFn,
        onSelectedUserEnteredAddressFn
    ) {
        if (title === null || title === undefined) {
            throw new Error("The argument\"title\" cannot be null/undefined.");
        } else if (recommendedAddresses === null || recommendedAddresses === undefined) {
            throw new Error("The argument \"recommendedAddresses\" cannot be null/undefined.");
        } else if (enteredAddress === null || enteredAddress === undefined) {
            throw new Error("The argument \"enteredAddress\" cannot be null/undefined.");
        } else if (onSelectedAddressFn === null || onSelectedAddressFn === undefined) {
            throw new Error("The argument \"onSelectedAddressFn\" cannot be null/undefined.");
        }

        let resolve = null;
        let reject = null;

        let $templateEl = $("#address-suggestion-template");
        let templateHtml = $templateEl.html();
        let $modal = $("#address-suggestions-modal");
        let $modalSuggestions = $modal.find(".suggested-addresses");
        let $submitModalBtn = $modal.find(".set-suggested-address");
        let $userEnteredAddress = $modal.find("#user-entered-address");

        // Remember the currently focused input.
        const $lastFocusedInput = $(":focus");

        // Always default to the user entered address.
        $userEnteredAddress.prop("checked", true);

        let result = new Promise((_resolve, _reject) => {
            resolve = _resolve;
            reject = _reject;
        });

        // Set the user entered address.
        $modal.find(".modal-title").text(title);
        $modal.find("#user-entered-address-line-1").text(enteredAddress.addressLine1 ?? "");
        $modal.find("#user-entered-address-line-2").text(enteredAddress.addressLine2 ?? "");
        $modal.find("#user-entered-city").text(enteredAddress.city ?? "");
        $modal.find("#user-entered-state").text(enteredAddress.stateProvince ?? "");
        $modal.find("#user-entered-postal-code").text(enteredAddress.postalCode ?? "");

        const $noResultsMessage = $modal.find(".no-suggested-addresses");
        const $suggestedAddressesContainer = $(".suggested-addresses-container");

        // Clear out the previous suggestions.
        $modalSuggestions.html("");

        const validAddresses = recommendedAddresses.filter(
            x => !(x.melissaDataResults?.some(x => x.resultCode.startsWith("GE")) ?? false)
                && (
                    // x.melissaDataResults.some(x => x.resultCode === "AE02")
                    (x.melissaDataResults?.some(y => y.resultCode === "AS01") ?? false)
                    || (x.melissaDataResults?.some(y => y.resultCode === "AS02") ?? false)
                    || (x.melissaDataResults?.some(y => y.resultCode === "AS03") ?? false)
                    || (x.melissaDataResults?.some(y => y.resultCode === "GS01") ?? false)
                )
                // Do not show the same address.
                && !areAddressesSame(x, enteredAddress)
        );

        if (validAddresses.length > 0) {
            $noResultsMessage.addClass("d-none");
            $suggestedAddressesContainer.removeClass("d-none");

            for (let i = 0; i < validAddresses.length; i++) {
                let suggestedAddress = recommendedAddresses[i];

                const addressLine1ChangedClass = suggestedAddress.addressLine1 !== enteredAddress.addressLine1 ? "text-warning" : "";
                const addressLine2ChangedClass = suggestedAddress.addressLine2 !== enteredAddress.addressLine2 ? "text-warning" : "";
                const cityChangedClass = suggestedAddress.city !== enteredAddress.city ? "text-warning" : "";
                const stateChangedClass = suggestedAddress.stateProvince !== enteredAddress.stateProvince ? "text-warning" : "";
                const postalCodeChangedClass = suggestedAddress.postalCode !== enteredAddress.postalCode ? "text-warning" : "";
                const countyChangedClass = suggestedAddress.county !== enteredAddress.county;

                let viewModel = {
                    suggestedAddressId: "suggested-address-" + (i + 1),
                    addressLine1: suggestedAddress.addressLine1,
                    addressLine1ChangedClass,
                    addressLine2: suggestedAddress.addressLine2,
                    addressLine2ChangedClass,
                    city: suggestedAddress.city,
                    cityChangedClass,
                    stateProvince: suggestedAddress.stateProvince,
                    stateChangedClass,
                    postalCode: suggestedAddress.postalCode,
                    postalCodeChangedClass,
                    county: suggestedAddress.county,
                    countyChangedClass,
                    country: suggestedAddress.country,
                    longitude: suggestedAddress.longitude,
                    latitude: suggestedAddress.latitude,
                    melissaAddressKey: suggestedAddress.melissaAddressKey,
                };

                let newHtml = bindViewModelToHtml(viewModel, templateHtml);
                $modal.find(".suggested-addresses").append(newHtml);
            }
        } else {
            $noResultsMessage.removeClass("d-none");
            $suggestedAddressesContainer.addClass("d-none");
        }

        $modal.modal({
            backdrop: true,
            // @ts-ignore
            show: true,
            keyboard: true,
        });

        $modal.modal("show");

        function onModalHidden(evt) {
            $submitModalBtn.off("click", onModalSetAddress);
            reject("dismissed");
        }

        function onModalSetAddress(evt) {
            $modal.off("hide.bs.modal", onModalHidden);
            $modal.modal("hide");

            const $selectedAddress = $modal.find("[name='SuggestedAddress']:checked");

            const addressLine1 = $selectedAddress.attr("data-address-line-1");
            const addressLine2 = $selectedAddress.attr("data-address-line-2");
            const city = $selectedAddress.attr("data-city");
            const county = $selectedAddress.attr("data-county");
            const postalCode = $selectedAddress.attr('data-postal-code');
            const stateProvince = $selectedAddress.attr("data-state-province");
            const country = $selectedAddress.attr("data-country");
            const melissaAddressKey = $selectedAddress.attr("data-melissa-address-key");
            const latitude = $selectedAddress.attr("data-latitude");
            const longitude = $selectedAddress.attr("data-longitude");

            /** @type {CodeAddress} */
            const address = {
                // @ts-ignore
                addressLine1,
                // @ts-ignore
                addressLine2,
                // @ts-ignore
                city,
                // @ts-ignore
                county,
                // @ts-ignore
                postalCode,
                // @ts-ignore
                stateProvince,
                // @ts-ignore
                country,
                // @ts-ignore
                melissaAddressKey,
                // @ts-ignore
                latitude,
                // @ts-ignore
                longitude,
            };

            // Short circuit if they are selecting the user entered address.
            if ($userEnteredAddress.is(":checked")) {
                onSelectedUserEnteredAddressFn(address);
                resolve();
                $lastFocusedInput[0]?.focus();
                return;
            }

            onSelectedAddressFn(address);
            resolve();
            $lastFocusedInput[0]?.focus();
        }

        $modal.one("hide.bs.modal", onModalHidden);
        $submitModalBtn.one("click", onModalSetAddress);

        return result;
    }

    /**
     * Retrieves the user entered address.
     * @param {JQuery<HTMLElement>} $addressContainer 
     * @returns {CodeAddress}
     */
    function getAddressFromForm($addressContainer) {
        const $addressLine1El = $addressContainer.find(".address-line-1");
        const $addressLine2El = $addressContainer.find(".address-line-2");
        const $postalCodeEl = $addressContainer.find(".postal-code");
        const $cityEl = $addressContainer.find(".city");
        const $countyEl = $addressContainer.find(".county");
        const $stateEl = $addressContainer.find(".state");
        const $country = $addressContainer.find(".country");
        const $melissaAddressKey = $addressContainer.find(".melissa-address-key");
        const $longitude = $addressContainer.find(".longitude");
        const $latitude = $addressContainer.find(".latitude");

        return {
            // @ts-ignore
            addressLine1: $addressLine1El.val().trim(),
            // @ts-ignore
            addressLine2: $addressLine2El.val().trim(),
            // @ts-ignore
            postalCode: $postalCodeEl.val().trim(),
            // @ts-ignore
            city: $cityEl.val().trim(),
            // @ts-ignore
            county: $countyEl.val().trim(),
            // @ts-ignore
            stateProvince: $stateEl.val().trim(),
            // @ts-ignore
            country: $country.val().trim(),
            // @ts-ignore
            melissaAddressKey: $melissaAddressKey.val().trim(),
            // @ts-ignore
            longitude: $longitude.val(),
            // @ts-ignore
            latitude: $latitude.val(),
        };
    }

    /**
     * Updates an address section with the address.
     * @param {JQuery<HTMLElement>} $addressContainer
     * @param {CodeAddress} address
     * @returns {void}
     */
    function setAddressFormSection($addressContainer, address) {
        const $addressLine1 = $addressContainer.find(".address-line-1");
        const $addressLine2 = $addressContainer.find(".address-line-2");
        const $postalCode = $addressContainer.find(".postal-code");
        const $city = $addressContainer.find(".city");
        const $state = $addressContainer.find(".state");
        const $county = $addressContainer.find(".county");
        const $country = $addressContainer.find(".country");
        const $melissaAddressKey = $addressContainer.find(".melissa-address-key");
        const $latitude = $addressContainer.find(".latitude");
        const $longitude = $addressContainer.find(".longitude");
        const $addressValidationOverride = $addressContainer.find(".address-validation-override");

        $addressLine1.val(address.addressLine1 ?? "");
        $addressLine1.attr("data-dirty", "true");
        $addressLine2.val(address.addressLine2 ?? "");
        $addressLine2.attr("data-dirty", "true");
        $postalCode.val(address.postalCode ?? "");
        $postalCode.attr("data-dirty", "true");
        $city.val(address.city ?? "");
        $city.attr("data-dirty", "true");
        $state.val(address.stateProvince ?? "");
        $state.attr("data-dirty", "true");
        $county.val(address.county ?? "");
        $county.attr("data-dirty", "true");
        $country.val(address.country ?? "");
        $country.attr("data-dirty", "true");
        $melissaAddressKey.val(address.melissaAddressKey ?? "");
        $melissaAddressKey.attr("data-dirty", "true");
        $latitude.val(address.latitude ?? "");
        $latitude.attr("data-dirty", "true");
        $longitude.val(address.longitude ?? "");
        $longitude.attr("data-dirty", "true");
        $addressValidationOverride.prop("checked", false);

        // If for some reason the melissaAddressKey is null/empty then set the
        // validation override to true.
        if ((address.melissaAddressKey ?? "")?.trim() === "") {
            $addressValidationOverride.prop("checked", true);
        }

        // @ts-ignore
        $addressLine1.valid();
        // @ts-ignore
        $addressLine2.valid();
        // @ts-ignore
        $postalCode.valid();
        // @ts-ignore
        $city.valid();
        // @ts-ignore
        $state.valid();
        // @ts-ignore
        $county.valid();
        // @ts-ignore
        $country.valid();
    }

    /**
     * Makes the server request for validating the request.
     * @param {AddressVerificationRequest} address 
     * @returns {Promise<AddressVerificationResponse>}
     */
    function validateAddressAsync(address) {
        if (address === null || address === undefined) {
            throw new Error("The argument \"address\" cannot be null/undefined.");
        }

        var request = new Request(addressVerifyUrl, {
            method: "POST",
            body: JSON.stringify(address),
            headers: {
                "Content-Type": "application/json",
            },
        });

        /** @type {Response|null} */
        let response = null;

        return fetch(request)
            .then(r => {
                response = r;

                if (response.ok) {
                    return response.json();
                } else {
                    return response.text();
                }
            })
            .then(responseContent => {
                if (response?.ok ?? false) {
                    return responseContent;
                } else {
                    throw new Error(responseContent);
                }
            });
    }

    /**
     * Makes the request for validating an address then shows a modal with the
     * suggested addresses if any. If no suggested addresses resolves
     * immediately. NOTE: This is a generic function for validating the primary
     * and secondary addresses. You probably want to use one of those methods
     * instead.
     * @param {string} title
     * @param {CodeAddress} address
     * @param {(arg0: CodeAddress) => void} onSelectedAddressFn
     * @param {(arg0: CodeAddress) => void} onSelectedUserEnteredAddressFn
     * @param {(arg0: CodeAddress) => void} onAutoUpdateAddressFn
     * @returns {Promise}
     * @see {@link conditionallyValidatePrimaryAddressAsync}
     * @see {@link conditionallyValidateSecondaryAddressAsync}
     */
    function showValidationModalForAddressIfNeededAsync(
        title,
        address,
        onSelectedAddressFn,
        onSelectedUserEnteredAddressFn,
        onAutoUpdateAddressFn
    ) {
        return validateAddressAsync({
            addressLine1: address.addressLine1 ?? "",
            addressLine2: address.addressLine2 ?? "",
            city: address.city ?? "",
            county: address.county ?? "",
            postalCode: address.postalCode ?? "",
            stateProvince: address.stateProvince ?? "",
        })
            .then(response => {
                const nextAction = determineMelissaVerificationActionV2(response, address);

                // If there are errors or the address requires user input, display the
                // melissa modal and wait for the users response.
                if ((nextAction.hasErrors || nextAction.requiresUserConfirmation)) {
                    return showAddressRecommendationsAsync(
                        title,
                        response.CodedAddresses,
                        address,
                        onSelectedAddressFn,
                        onSelectedUserEnteredAddressFn
                    );
                } else {
                    // If reaching this step then the address is valid and no
                    // user action is needed.
                    onAutoUpdateAddressFn(response.CodedAddresses[0]);
                    return Promise.resolve();
                }
            })
            .catch(error => {
                if (error === "dismissed") {
                    throw error;
                } else {
                    showError({titleHtml: "Failed to validate the address."});
                    console.error(error);
                }
            });
    }

    /**
     * A function which conditionally shows suggested addresses modal for the
     * primary address. Will not show the modal if the address was already
     * validated and has not changed since last validation.
     * @returns {Promise<boolean>}
     */
    function conditionallyValidatePrimaryAddressAsync() {
        // Make sure the primary address was validated.
        if (primaryAddressValidated) {
            return Promise.resolve(true);
        }

        let userEnteredAddress = getAddressFromForm($primaryAddressContainer);
        $primaryAddressValidatedOverride.prop("checked", false);

        return showValidationModalForAddressIfNeededAsync(
            verifyPrimaryAddressModalTitle,
            userEnteredAddress,
            newAddress => setAddressFormSection($primaryAddressContainer, newAddress),
            () => $primaryAddressValidatedOverride.prop("checked", true),
            codedAddress => {
                setAddressFormSection($primaryAddressContainer, codedAddress);
                $primaryAddressValidatedOverride.prop("checked", false);
            }
        )
            .then(() => primaryAddressValidated = true)
            .catch(error => {
                // Short circuit if the user dismisses the modal or some other
                // error.
                primaryAddressValidated = false;
                $primaryMelissaAddressKey.val("");
                $primaryLatitude.val("");
                $primaryLongitude.val("");

                throw error;
            });
    }

    /**
     * Makes the server request for autocompleting the address.
     * @param {AddressAutofillRequest} address 
     * @param {AbortSignal|null} abortSignal
     * @returns {Promise<CodeAddress>}
     */
    function autoCompleteAddressAsync(address, abortSignal) {
        if (address === null || address === undefined) {
            throw new Error("The argument \"address\" cannot be null/undefined.");
        }

        let request = new Request(addressAutoCompleteUrl, {
            method: "POST",
            body: JSON.stringify(address),
            headers: {
                "Content-Type": "application/json",
            },
            signal: abortSignal,
        });

        /** @type {Response|null} */
        let response = null;

        return fetch(request)
            .then(r => {
                response = r;

                if (response.ok) {
                    return response.json();
                } else {
                    return response.text();
                }
            })
            .then(responseContent => {
                if (response?.ok ?? false) {
                    return responseContent;
                } else {
                    throw new Error(responseContent);
                }
            });
    }

    //#endregion

    //#region Form Validation
    //This is using jquery-validation plug in
    //https://jqueryvalidation.org/

    // @ts-ignore
    jQuery.validator.addMethod("sp_postal_code", function (value, element) {
        const postalCodeRegex = /^[0-9]{5,5}$/gi;
        return this.optional(element) || postalCodeRegex.test(value);
    }, "Please enter a valid Zip Code");

    // @ts-ignore
    jQuery.validator.addMethod("sp_url", function (value, element) {
        const isOptional = this.optional(element);

        if (!isOptional) {
            return isOptional;
        }

        let url;

        try {
            url = new URL(value);

            return url.protocol === "http:" || url.protocol === "https:";
        } catch {
            return false;
        }
    }, "Please enter a valid Zip Code");

    // @ts-ignore
    jQuery.validator.addMethod("sp_fax", function (value, element) {
        const faxRegex = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/gi;
        return this.optional(element) || faxRegex.test(value);
    }, "Fax is not valid");

    // @ts-ignore
    jQuery.validator.addMethod("sp_server_message", function (value, element, params) {
        const elementName = $(element).attr("name");
        // @ts-ignore
        return !(elementName in serverValidationMessages);
    }, function (params, element) {
        const elementName = $(element).attr("name");
        let serverErrorMessage = serverValidationMessages[elementName];

        if (serverErrorMessage.indexOf("{{") > 0 && serverErrorMessage.indexOf("}}") > 0) {
            serverErrorMessage = bindViewModelToHtml($(element).data(), serverErrorMessage);
        }

        return serverErrorMessage || "Unknown server error.";
    });

    // NOTE: False shows the validation, true does not
    // @ts-ignore
    jQuery.validator.addMethod("sp_primaryphonenumber", function (value, element, params) {
        if (this.optional(element) == true) {
            return true;
        }
        //If the value is empty or an empty string, disable Primary Phone Type inputs
        if (value.trim().length <= 0) {
            $(element).val($(element).val().trim());
            $(element).parents('.js-primary-phone-number').find('.phone-types input').attr('disabled', 'disabled')
            return false;
        }
        //Otherwise, show the inputs
        $(element).parents('.js-primary-phone-number').find('.phone-types input').removeAttr('disabled')
        return true;
    });

    // NOTE: False shows the validation, true does not
    // @ts-ignore
    jQuery.validator.addMethod("sp_secondaryphonenumber", function (value, element, params) {

        //If the value is empty or an empty string, disable Primary Phone Type inputs
        if (value.trim().length <= 0) {
            $(element).val($(element).val().trim());
            $(element).parents('.js-secondary-phone-number').find('.phone-types input').attr('disabled', 'disabled')
            return true;
        }
        //Otherwise, show the inputs
        $(element).parents('.js-secondary-phone-number').find('.phone-types input').removeAttr('disabled')
        return true;
    });

    // Override the focusInvalid method on the $validator object so that it
    // scrolls to the FIRST invalid element and not the last.
    // Original Source: https://github.com/jquery-validation/jquery-validation/blob/7ba6372c04705a8b90101deec1b0561f12a02518/src/core.js#L607
    // @ts-ignore
    jQuery.validator.prototype.focusInvalid = function () {
        if (this.settings.focusInvalid) {
            try {
                $(this.errorList.length && this.errorList[0].element || [])
                    .filter(":visible")
                    .trigger("focus")

                    // Manually trigger focusin event; without it, focusin handler isn't called, findLastActive won't have anything to find
                    .trigger("focusin");
            } catch (e) {

                // Ignore IE throwing errors when focusing hidden elements
            }
        }
    };

    // NOTE: Requires the parameters "firstName" and "lastName".
    // @ts-ignore
    jQuery.validator.addMethod("sp_fullname", function (value, element, params) {
        if (this.optional(element)) {
            return true;
        }

        const firstName = $(params.firstName).val();
        const lastName = $(params.lastName).val();
        const expectedFullName = `${firstName} ${lastName}`;

        return value === expectedFullName;
    }, "The name entered should match the applicant's name");


    // NOTE: Requires the parameters "firstName" and "lastName".
    // @ts-ignore
    jQuery.validator.addMethod("sp_includesfirstnamelastname", function (value, element, params) {
        if (this.optional(element)) {
            return true;
        }
        const firstName = $(params.firstName).val();
        const lastName = $(params.lastName).val();
        const fullName = `${firstName} ${lastName}`.replace(/\s+/g, " ").toLowerCase();
        // @ts-ignore
        const signature = $("#AgreementFullName").val().trim();

        // Normalize the signature value for comparison
        const normalizedSignature = signature.replace(/\s+/g, " ").toLowerCase();

        return normalizedSignature === fullName;
    }, "The name entered should match the applicant's name");
    // This will mark the element as invalid if the pattern does not match.
    // NOTE: Requires the parameters "positivePattern".
    // @ts-ignore
    jQuery.validator.addMethod("sp_positive_pattern", function (value, element, params) {
        if (this.optional(element)) {
            return true;
        }

        if ((params.positivePattern ?? "") === "") {
            return false;
        }

        const regex = new RegExp(params.positivePattern, "gim");
        const result = regex.exec(value);

        return result !== null;
    }, "Invalid characters.");

    // @ts-ignore
    jQuery.validator.addMethod("sp_datalist", function (value, element, params) {
        const $element = $(element);
        const $dropdownContainer = $($element.attr("data-sp-dropdown"));

        if ($dropdownContainer.length === 0) {
            // Error, failed to identify the dropdown container.
            console.warn("Failed to identify the dropdown container for the element", element);
            return false;
        }

        const $selectedOption = $dropdownContainer.children(".active");

        if ($selectedOption.length === 0) {
            return false;
        }

        const result = $selectedOption.text() === $element.val();

        // False shows the validation, true does not
        return result;
    }, "Select a record from the list.");

    const alphaAndDashPeriodPattern = "^[a-z-\\.'\\s]*$";
    const alphaNumericPatternAndSpace = "^[a-z0-9\\s]*$";
    const alphaNumericPatternAndPoundDotSpace = "^[a-z0-9#!&$%^<>\\.?'~\\s\\/\\\\-]*$";
    const alphaNumericAndAmpersandPeriodDashPattern = "^[a-z0-9-_\\.&\\s']*$";
    const emailPattern = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$";
    const addressPattern = "^[a-z0-9-_\\.&\\s'/#]*$";

    // @ts-ignore
    const $validator = $form.validate({
        onfocusout: function (element) {
            // @ts-ignore
            $(element).valid();
        },
        onkeyup: function (element, event) {
            if ($(element).is(".no-validate-on-key-down")) {
                return;
            }

            // @ts-ignore
            $.validator.defaults.onkeyup.call(this, element, event);
        },
        rules: {
            // Account Holder Details
            FirstName: {
                required: true,
                maxlength: 35,
                sp_server_message: true,
                sp_positive_pattern: {
                    positivePattern: alphaAndDashPeriodPattern,
                },
            },
            LastName: {
                required: true,
                maxlength: 35,
                sp_server_message: true,
                sp_positive_pattern: {
                    positivePattern: alphaAndDashPeriodPattern,
                },
            },
            EmailAddress: {
                required: true,
                email: true,
                sp_positive_pattern: {
                    positivePattern: emailPattern,
                },
                sp_server_message: true,
            },
            ConfirmEmailAddress: {
                equalTo: "#EmailAddress",
                sp_server_message: true,
            },
            PrimaryPhoneNumber: {
                required: element => {
                    if ($(element).val().length <= 0) {
                        $(element).parents('.js-primary-phone-number').find('.phone-types input').attr('disabled', 'disabled').prop('checked', false);
                    }
                    return true
                },
                sp_server_message: true,
                sp_primaryphonenumber: {
                    primaryphonenumber: "#PrimaryPhoneNumber",
                },
            },
            PrimaryPhoneType: {
                required: true,
                sp_server_message: true,
            },
            SecondaryPhoneNumber: {
                required: element => {
                    if ($(element).val().length <= 0) {
                        $(element).parents('.js-secondary-phone-number').find('.phone-types input').attr('disabled', 'disabled').prop('checked', false);
                    }
                    return false;
                },
                sp_server_message: true,
                sp_secondaryphonenumber: {
                    secondaryphonenumber: "#SecondaryPhoneNumber",
                },
            },
            SecondaryPhoneType: {
                required: element => {
                    // @ts-ignore
                    return $secondaryPhoneNumber.val().trim().length > 0;
                },
                sp_server_message: true,
                
            },
            TextMessageOptIn: {
                sp_server_message: true,
            },

            // Primary Shipping Address
            PrimaryAddressLine1: {
                required: true,
                sp_server_message: true,
                sp_positive_pattern: {
                    positivePattern: addressPattern,
                },
            },
            PrimaryAddressLine2: {
                sp_server_message: true,
                sp_positive_pattern: {
                    positivePattern: addressPattern,
                },
            },
            PrimaryPostalCode: {
                required: true,
                maxlength: 5,
                sp_postal_code: true,
                sp_server_message: true,
            },
            PrimaryCity: {
                required: true,
                sp_server_message: true,
                sp_positive_pattern: {
                    positivePattern: alphaNumericPatternAndSpace,
                },
            },
            PrimaryState: {
                required: true,
                sp_server_message: true,
            },
            PrimaryCounty: {
                required: true,
                sp_server_message: true,
                sp_positive_pattern: {
                    positivePattern: alphaNumericPatternAndSpace,
                },
            },
            PrimaryAddressIsResidential: {
                sp_server_message: true,
            },
            Website: {
                url: true,
                sp_server_message: true,
            },
            Fax: {
                sp_fax: true,
                sp_server_message: true,
            },

            CollegeAttendedIfsKey: {
                sp_datalist: true,
                sp_server_message: true,
            },
            DegreeIfsKey: {
                sp_datalist: true,
                sp_server_message: true,
            },

            // Communications
            SubscribeToEducationAndEvents: {
                sp_server_message: true,
            },
            SubscribeToNews: {
                sp_server_message: true,
            },
            AdditionalQuestions: {
                sp_server_message: true,
            },

            // Agreement
            AgreementFullName: {
                required: true,
                sp_includesfirstnamelastname: {
                    firstName: "#FirstName",
                    lastName: "#LastName",
                },
                sp_server_message: true,
            },
        },
        errorElement: "label",
        errorClass: "alert-msg",
        messages: {
            FirstName: {
                required: "First name is required",
            },
            LastName: {
                required: "Last name is required",
            },
            BusinessName: {
                required: "Business Name is required",
            },
            EmailAddress: {
                required: "Email is required",
                email: "Please enter a valid email address",
                sp_positive_pattern: "Please enter a valid email address",
            },
            ConfirmEmailAddress: {
                required: "Email confirmation is required",
                equalTo: "Email entry does not match",
            },
            PrimaryAddressLine1: {
                required: "Street address is required",
            },
            PrimaryPostalCode: {
                required: "Zip code is required",
                sp_postal_code: "Please enter a valid Zip Code",
            },
            PrimaryCity: {
                required: "City is required",
            },
            PrimaryState: {
                required: "State is required",
            },
            PrimaryCounty: {
                required: "County is required",
            },
            PrimaryPhoneNumber: {
                required: "Phone Number is required",
            },
            PrimaryPhoneType: {
                required: "Selection is required",
            },
            SecondaryPhoneType: {
                required: "Selection is required",
            },
            Website: {
                url: "Website is not valid",
            },
            Fax: {
                sp_fax: "Fax is not valid",
            },
            CollegeAttendedIfsKey: {
                required: "College is required",
                sp_datalist: "College is required",
            },
            DegreeIfsKey: {
                required: "Degree is required",
                sp_datalist: "Degree is required",
            },
            LicenseCertificateNumber: {
                required: "License/Certificate Number is required",
            },
            AgreementFullName: {
                required: "Full name is required",
                sp_fullname: "The name entered should match the applicant's name",
            },
        },
        errorPlacement: function ($errorEl, $inputEl) {
            if ($inputEl.is("[type='radio']")) {
                const $validationContainer = $inputEl.closest(".custom-sp-label-wrapper").find(".validation");
                $errorEl.appendTo($validationContainer);
            } else {
                $errorEl.appendTo($inputEl.parent());
            }
        },
        highlight: function (element, errorClass, validClass) {
            const $element = $(element);
            const $phoneTypeContainer = $element.closest(".phone-types");
            const isIntendedUse = $element.is("[name='IntendedUse']");

            if ($phoneTypeContainer.length > 0) {
                if ((errorClass ?? "").length > 0) {
                    $phoneTypeContainer.addClass(errorClass);
                }

                if ((validClass ?? "").length > 0) {
                    $phoneTypeContainer.removeClass(validClass);
                }
            } else {
                if ((errorClass ?? "").length > 0) {
                    $element.addClass(errorClass);
                }

                if ((validClass ?? "").length > 0) {
                    $element.removeClass(validClass);
                }
            }

            if (isIntendedUse) {
                if ((errorClass ?? "").length > 0) {
                    $intendedUses.addClass(errorClass);
                }

                if ((validClass ?? "").length > 0) {
                    $intendedUses.removeClass(validClass);
                }
            }
        },
        unhighlight: function (element, errorClass, validClass) {
            const $element = $(element);
            const $phoneTypeContainer = $element.closest(".phone-types");
            const isIntendedUse = $element.is("[name='IntendedUse']");

            if ($phoneTypeContainer.length > 0) {
                if ((errorClass ?? "").length > 0) {
                    $phoneTypeContainer.removeClass(errorClass);
                }

                if ((validClass ?? "").length > 0) {
                    $phoneTypeContainer.addClass(validClass);
                }
            } else {
                if ((errorClass ?? "").length > 0) {
                    $element.removeClass(errorClass);
                }

                if ((validClass ?? "").length > 0) {
                    $element.addClass(validClass);
                }
            }

            if (isIntendedUse) {
                if ((errorClass ?? "").length > 0) {
                    $intendedUses.removeClass(errorClass);
                }

                if ((validClass ?? "").length > 0) {
                    $intendedUses.addClass(validClass);
                }
            }
        },
        success: function ($label) {
            // Helper for removing the error class from the custom
            // radio container.
            $label
                .closest(".custom-sp-label-wrapper")
                .find(".labelled-container.error")
                .removeClass("error");
        },
        invalidHandler: function (event, validator) {
            // Helper for adding the error class to the 
            // radio container.
            for (let errorEntry of validator.errorList) {
                var $parent = $(errorEntry.element).closest(".labelled-container");

                if ($parent.length === 0) {
                    continue;
                }

                $parent.addClass(validator.settings.errorClass);
            }
        },
    });

    //#endregion

    //#region Misc

    /**
     * @returns {Request}
     */
    function generateCreateAccountRequest() {
        // If the melissaAddressKey is null/empty then ensure the validation
        // override is true.
        const primaryMelissaAddressKey = ($primaryMelissaAddressKey.val()?.toString() ?? "").trim();
        const primaryValidationOverride = $primaryAddressValidatedOverride.prop("checked");

        if (primaryMelissaAddressKey === "" && !primaryValidationOverride) {
            $primaryAddressValidatedOverride.prop("checked", true);
        }

        // This is a work around for the following code:
        // "const urlSearchParams = new URLSearchParams(new FormData($form[0]));"
        // Not sure why the inner form data is empty.
        const formData = {};

        $form.find("[name]").each(function () {
            const $el = $(this);
            const type = $el.attr("type");
            const isCheckbox = type === "checkbox";
            const isRadio = type === "radio";
            const isChecked = $el.is(":checked");
            const useOptionValue = $el.is(".js-datalist");

            if (useOptionValue) {
                const dropDownContainerSelector = $el.attr("data-sp-dropdown");
                const $selectedOpt = $(dropDownContainerSelector).children(".active").first();
                const value = $selectedOpt.attr("value");
                formData[$el.attr("name")] = value;
            } else if (isCheckbox && isChecked) {
                formData[$el.attr("name")] = $el.val();
            } else if (isRadio && isChecked) {
                formData[$el.attr("name")] = $el.val();
            } else if (!isCheckbox && !isRadio) {
                formData[$el.attr("name")] = $el.val();
            }
        });

        // @ts-ignore
        const urlSearchParams = new URLSearchParams(formData);
        // End work around.

        // @ts-ignore
        const registerRequest = new Request($form.attr("action"), {
            method: "POST",
            body: urlSearchParams,
        });

        return registerRequest;
    }

    /**
     * Tries to format the value into a phone number.
     * @param {string} value 
     * @returns {string}
     */
    function formatStringToPhoneNumber(value) {
        const validRegex = /^\d{3}-\d{3}-\d{4}$/g;
        const result = validRegex.exec(value);

        // If already in a valid format, short circuit.
        if (result !== null) {
            return value;
        }

        // Remove all non-numbers.
        value = value.replace(/\D/g, "");
        let numberCount = value.length;

        // If there are more than 10 numbers, take the last 10.
        if (numberCount > 10) {
            value = value.substring(value.length - 10, value.length);
            numberCount = 10;
        }

        // Insert dashes at appropriate places.
        if (numberCount > 6) {
            // Need two dashes.
            value = value.substring(0, 3)
                + "-"
                + value.substring(3, 6)
                + "-"
                + value.substring(6, value.length);
        } else if (numberCount > 3) {
            // Need one dash.
            value = value.substring(0, 3)
                + "-"
                + value.substring(3, 6);
        }

        return value;
    }

    /**
     * Displays a toast error message that automatically closes in 30 seconds.
     * @param {object} data
     * @param {string} data.titleHtml
     * @param {string} [data.titleClass] Defaults to "text-danger".
     * @param {string} [data.bodyHtml] Defaults to empty.
     * @param {string} [data.bodyClass] Defaults to "text-danger".
     * @param {number} [data.timeoutMS] Defaults to 30 seconds. Pass in 0 for
     * it to never timeout.
     * @returns {void}
     */
    function showError(data) {
        if (data === null || data === undefined) {
            throw new Error("The argument \"data\" cannot be null/undefind.");
        }

        const $toastContainer = $("#GeneralErrorToastContainer");

        if ($toastContainer.length === 0) {
            throw new Error("Failed to locate the #GeneralErrorToastContainer.");
        }

        /** @type {HTMLTemplateElement} */
        // @ts-ignore
        const toastTemplateEl = $("#GeneralErrorToastTemplate")[0];

        /** @type {HTMLDivElement} */
        // @ts-ignore
        let toastCloneEl = toastTemplateEl.content.cloneNode(true);

        const toastTitleEl = toastCloneEl.querySelector(".toast-title");
        const toastBodyEl = toastCloneEl.querySelector(".toast-body");
        const dismissButtonEl = toastCloneEl.querySelector("[data-bs-dismiss='toast']");

        if (toastTitleEl === null || toastTitleEl === undefined) {
            throw new Error("Failed to locate the toast-title element.");
        } else if (toastBodyEl === null || toastBodyEl === undefined) {
            throw new Error("Failed to locate the toast-body element.");
        } else if (dismissButtonEl === null || dismissButtonEl === undefined) {
            throw new Error("Failed to locate the element matching the selector \"[data-bs-dismiss='toast']\".");
        }

        toastTitleEl.innerHTML = data.titleHtml;
        toastTitleEl.classList.add(data.titleClass ?? "text-danger");

        if (data.bodyHtml) {
            toastBodyEl.innerHTML = data.bodyHtml;
            toastBodyEl.classList.add(data.bodyClass ?? "text-danger");
        } else {
            toastBodyEl.remove();
        }

        // Insert the toast.
        $toastContainer[0].appendChild(toastCloneEl);

        // @ts-ignore
        toastCloneEl = $toastContainer.children(":last-child")[0];

        // Default to 30 hide to seconds.
        const autoDismissTimeoutMS = data?.timeoutMS ?? 30000;

        // Initialize the Bootstrap Toast.
        const bootstrapToast = new bootstrap.Toast(toastCloneEl, {
            animation: true,
            autohide: autoDismissTimeoutMS > 0,
            delay: autoDismissTimeoutMS,
        });

        bootstrapToast.show();

        // Remove the toast element once hidden.
        toastCloneEl.addEventListener(
            "hidden.bs.toast",
            () => toastCloneEl.remove(),
            { once: true, passive: true, }
        );
    }

    /**
     * Checks if the response is a ModelState response.
     * @param {*} obj 
     * @returns {obj is {[key: string]: string[]}}
     */
    function isModelStateError(obj) {
        return obj !== null
            && obj !== undefined
            && Object.keys(obj).every(key => Array.isArray(obj[key]));
    }

    /**
     * Used for disabling user input.
     * @param {boolean} blocking True to disable elements, false to enable user
     * interaction.
     */
    function blockUI(blocking) {
        if (blocking === null || blocking === undefined) {
            throw new Error("The argument \"blocking\" cannot be null/undefined.");
        }

        if (blocking) {
            $submitBtn.addClass('disabled');
            $loadingIndicator.removeClass('d-none');

            const $elements = $("#studentRegistrationForm input:not([disabled]),#studentRegistrationForm button:not([disabled]),#studentRegistrationForm select:not([disabled]),#studentRegistrationForm textarea:not([disabled])");

            $elements
                .prop("disabled", true)
                .addClass("disabled")
                .attr("data-unblock", "true");
        } else {
            $submitBtn.removeClass('disabled');
            $loadingIndicator.addClass('d-none');

            const $elements = $("#studentRegistrationForm *[data-unblock]");

            $elements
                .prop("disabled", false)
                .removeClass("disabled")
                .removeAttr("data-unblock");
        }
    }

    /**
     * Populates the template.
     * @param {object} viewModel Must be an object, cannot be a primitive (like
     * a string).
     * @param {string} html 
     * @returns {string}
     */
    function bindViewModelToHtml(viewModel, html) {
        let htmlCopy = html;
        const regex = /{{(.*?)}}/gi;
        const viewModelKeys = Object.keys(viewModel);

        for (let matchArr of html.matchAll(regex)) {
            const htmlKey = matchArr[0];
            const expression = "return this." + matchArr[1];
            const fnBinder = new Function(expression);
            const replacementHtml = fnBinder.call(viewModel);

            while (htmlCopy.includes(htmlKey)) {
                htmlCopy = htmlCopy.replace(htmlKey, replacementHtml);
            }
        }

        return htmlCopy;
    }

    function onLeaveAttempt(evt) {
        var hasChanges = false;

        $form.find("input,textarea,select").each(function () {
            const $inputEl = $(this);

            // @ts-ignore
            if ($inputEl.val().toString().trim() !== "") {
                hasChanges = true;
            }
        });

        if (hasChanges) {
            // NOTE: Currently the beforeunload event ONLY allows for a
            // browser specific message to be displayed when navigating away.
            // So no custom modals yet.
            evt.preventDefault();
            return (evt.returnValue = "");
        }
    }

    //#endregion

    //#region Event Handlers

    // Focus the first name input on document ready.
    $firstName[0].focus();

    // Allow tabbing thru individual radio buttons.
    $("input[type='radio']").on("keydown", function (evt) {
        if (evt.key !== "Tab") {
            return;
        }

        const $this = $(this);
        const radioGroupName = $this.attr("name");
        const $allRelatedRadios = $(`input[type='radio'][name='${radioGroupName}']`);
        const selectedIndex = $allRelatedRadios.index($this);

        // If on the first radio element and navigating backwards short
        // circuit.
        if (selectedIndex === 0 && evt.shiftKey) {
            return;
        }
        // If on the last radio element and navigating forwards short circuit.
        else if ((selectedIndex === ($allRelatedRadios.length - 1)) && !evt.shiftKey) {
            return;
        }

        evt.stopImmediatePropagation();
        evt.preventDefault();

        if (evt.shiftKey) {
            // Navigating backwards.
            $allRelatedRadios[selectedIndex - 1].focus();
        } else {
            // Navigating forwards.
            $allRelatedRadios[selectedIndex + 1].focus();
        }
    });

    // On the user entering the address-line 1 and postal code, auto-complete
    // the address.
    // NOTE: SHOULD ONLY auto-complete if any of the city/state/county inputs
    // are empty.
    $("#PrimaryAddressLine1,#PrimaryPostalCode").on("change", function (evt) {
        const $addressSection = $(this).closest(".address-container");
        const address = getAddressFromForm($addressSection);

        /** @type {HTMLElement|null} */
        let nextEl = null;

        if (evt.target.matches(".address-line-1")) {
            nextEl = $addressSection.find(".address-line-2")[0];
        } else if (evt.target.matches(".postal-code")) {
            nextEl = $addressSection.find(".city")[0];
        } else {
            throw new Error("Unable to determine next focusable element.");
        }

        const hasMinimumRequiredFields = address.addressLine1 !== ""
            && address.postalCode !== "";

        if (!hasMinimumRequiredFields) {
            return;
        }

        const missingCityStateCounty = address.city === ""
            || address.county === ""
            || address.stateProvince === "";

        // If all of the city/state/county/addressLine1/addressLine2 inputs are
        // entered then just validate the address and short-circuit.
        if (!missingCityStateCounty) {
            validatePrimaryAddress();
            return;
        }

        blockUI(true);

        autoCompleteAddressAsync(
            {
                addressLine1: address.addressLine1 ?? "",
                addressLine2: address.addressLine2,
                postalCode: address.postalCode ?? "",
                city: address.city,
                stateProvince: address.stateProvince,
                county: address.county,
            },
            null
        )
            .then(completeAddress => {
                setAddressFormSection($addressSection, completeAddress);
                validatePrimaryAddress();
            })
            .catch(error => {
                showError({ titleHtml: "Error autocompleting the address." });
                console.error(error);
            })
            .finally(() => {
                blockUI(false);
                nextEl?.focus();
            });
    });

    // This checks that the Melissa Data Address modal
    // allows the user to only have one radio button selected
    // when chosing to use the 'Entered' or 'Suggested' address
    $(document).on('click', '#user-entered-address, #suggested-address-1', function (event) {
        let $modal = $("#address-suggestions-modal");

        if (event.target.id === 'user-entered-address') {
            let $suggestedAddress = $modal.find('#suggested-address-1');
            if ($suggestedAddress.is(':checked')) {
                $suggestedAddress.prop("checked", false);
            }
        } else if (event.target.id === 'suggested-address-1') {
            let $userEnteredAddress = $modal.find("#user-entered-address");
            if ($userEnteredAddress.is(':checked')) {
                $userEnteredAddress.prop("checked", false);
            }
        }
    });


    // This is for marking which inputs have been touched by the user.
    $allFormInputs.one("focusin", function (evt) {
        const $this = $(this);
        $this.attr("data-dirty", "true");
    });

    // Insert a dash based on the length of the string for the phone elements.
    $("input[type='tel']").on("keydown", function (evt) {
        var $input = $(this);
        // @ts-ignore
        var val = $input.val().trim();

        if (evt.key === "Backspace" || evt.key === "Delete" || evt.key === "-") {
            return;
        }

        if (val.length === 3) {
            val += "-";
        } else if (val.length === 7) {
            val += "-";
        }

        $input.val(val);
    });

    // Try and format the phone-number value after the value changes.
    $("input[type='tel']").on("change", function (evt) {
        const $this = $(this);

        /** @type {string} */
        // @ts-ignore
        let value = $this.val();

        value = formatStringToPhoneNumber(value);
        $this.val(value);
    });

    // Try and format the phone-number value after pasting content.
    $("input[type='tel']").on("paste", function (evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        const $this = $(this);

        /** @type {string} */
        // @ts-ignore
        let value = evt.originalEvent.clipboardData.getData("text");

        value = formatStringToPhoneNumber(value);
        $this.val(value);
    });

    // This clears out any stored server validation messages whenever any of
    // the inputs change.
    $form.find("input,select,textarea").on("change", function (evt) {
        var $targetEl = $(evt.target);
        var elName = $targetEl.attr("name") ?? "";

        if (elName in serverValidationMessages) {
            delete serverValidationMessages[elName];
        }
    });

    // Perform address validation after the user has entered last required
    // primary address input. Ignore the address-line-1 and postal code so it
    // does not conflict with the auto-complete handler.
    $primaryAddressContainer.find("input:not(#PrimaryAddressIsResidential):not(.address-line-1):not(.postal-code),select").on("change", function (evt) {
        validatePrimaryAddress();
    });

    // Handler for if the user tries to navigate away while they have changes.
    $allFormInputs.on("change", function (evt) {
        const hasChanges = $allFormInputs.filter(function (index, element) {
            // @ts-ignore
            return $(element).val().toString().trim() !== "";
        }).length > 0;

        if (hasChanges && !addedBeforeUnloadEventHandler) {
            window.addEventListener("beforeunload", onLeaveAttempt);
            addedBeforeUnloadEventHandler = true;
        } else if (!hasChanges && addedBeforeUnloadEventHandler) {
            window.removeEventListener("beforeunload", onLeaveAttempt);
            addedBeforeUnloadEventHandler = false;
        }
    });

    // The submit handler for the form. Short circuits on any form errors.
    // NOTE: This is called by recaptcha after the user clicks the form submit
    // button.
    // @ts-ignore
    window.registrationFormSubmit = function () {
        let encounteredError = false;

        // If any client errors short circuit.
        if (!$validator.form()) {
            $validator.focusInvalid();
            return;
        }

        const serverValidationMessagesKeys = Object.keys(serverValidationMessages);

        // Clear out all server validation messages.
        for (let key of serverValidationMessagesKeys) {
            delete serverValidationMessages[key];
        }

        blockUI(true);

        setTimeout(() => {
            conditionallyValidatePrimaryAddressAsync()
                .then(() => fetch(generateCreateAccountRequest()))
                .then(response => {
                    if (response.ok) {
                        // Emit the GA4 event.
                        var signupObj = {
                            event: "sign_up",
                            method: "HCP"
                        }

                        window.dataLayer = window.dataLayer || [];
                        // @ts-ignore
                        window.dataLayer.push(signupObj);

                        // Remove the beforeunload handler if it was added.
                        if (addedBeforeUnloadEventHandler) {
                            window.removeEventListener("beforeunload", onLeaveAttempt);
                        }

                        // Navigate to the new location.
                        window.location.assign(response.url);
                        return;
                    }

                    // Handle the error.
                    encounteredError = true;
                    return response.json();
                })
                .then(jsonError => {
                    // This block is for parsing any ModelState errors returned
                    // from the server.
                    if (!encounteredError) {
                        return;
                    }

                    // First check if there are any keys in the response. If zero
                    // keys in the response object then display a generic error
                    // message.
                    if (isModelStateError(jsonError)) {
                        // Check if the error is with Melissa data validation.
                        const hasMelissaAddressValidationError = jsonError["AgreementFullName"]
                            ?.some(x => x === 'Melissa Data validation is required.') ?? false;

                        if (hasMelissaAddressValidationError) {
                            const primaryMelissaAddressKey = ($primaryMelissaAddressKey.val()?.toString() ?? "").trim();
                            const primaryValidationOverride = $primaryAddressValidatedOverride.prop("checked");

                            if (primaryMelissaAddressKey === "" && !primaryValidationOverride) {
                                // Issue was with the primary address.
                                primaryAddressValidated = false;
                                $primaryMelissaAddressKey.val("");
                                $primaryLatitude.val("");
                                $primaryLongitude.val("");
                                serverValidationMessages["PrimaryAddressLine1"] = "Unable to determine if address is deliverable.";
                            }
                        } else {
                            for (let errorKey in jsonError) {
                                const errorValueArr = jsonError[errorKey];
                                const errorValueHtml = errorValueArr.join("<br>");
                                serverValidationMessages[errorKey] = errorValueHtml;
        
                                // Re-run the validator for that element.
                                const $formInputEl = $(`#${errorKey}`);
        
                                if ($formInputEl.length > 0) {
                                    // @ts-ignore
                                    $formInputEl.valid();
                                }
                            }
                        }
                    } else {
                        serverValidationMessages["AgreementFullName"] = "Error occurred. Please reload the page and try again.";

                        // Re-run the validator for that element.
                        const $formInputEl = $(`#AgreementFullName`);

                        if ($formInputEl.length > 0) {
                            // @ts-ignore
                            $formInputEl.valid();
                        }
                    }

                    // Have zero clue why wrapping this in a timeout gets
                    // sp_server_message to work...
                    setTimeout(() => {
                        $validator.form();
                        $validator.focusInvalid();
                    }, 1);
                })
                .catch(error => {
                    // Only log the error if it is not a dismiss error.
                    if (error !== null && error !== undefined && error !== "dismiss") {
                        console.error(error);
                    }
                })
                .finally(() => blockUI(false));
        }, 1)
    }

    //#endregion


    $(document).ready(function () {
        var collegeInput = $('#CollegeAttendedIfsKey');
        var collegeDataList = $('#collegeList');
        var collegeCurrentFocus = -1;

        var degreeInput = $('#DegreeIfsKey');
        var degreeDataList = $('#degreeOptionsList');
        var degreeCurrentFocus = -1;

        collegeDataList.hide();
        degreeDataList.hide();

        function handleInput(input, dataList, currentFocus) {
            input.on('focus', function () {
                dataList.css('display', 'block');
            });

            input.on('blur', function () {
                setTimeout(function () {
                    dataList.css('display', 'none');
                }, 200);
            });

            dataList.on('click', '[value]', function () {
                input.val($(this).text());
                addActive($(this));
                dataList.css('display', 'none');
                // @ts-ignore
                $(input).valid()
            });

            input.on('click', function () {
                const $dropdown = $($(this).attr("data-sp-dropdown"));
                $dropdown.show();
            });

            input.on('input', function () {
                currentFocus = -1;
                var text = input.val().toUpperCase();
                dataList.children('[value]').each(function () {
                    var optionText = $(this).text().toUpperCase();
                    $(this).toggle(optionText.indexOf(text) > -1);
                });
            });

            input.on('keydown', function (e) {
                var key = e.which || 0;
                if (key == 40 || key == 38) {
                    e.preventDefault();
                    currentFocus += key == 40 ? 1 : -1;
                    var options = dataList.children('[value]');
                    currentFocus = (currentFocus + options.length) % options.length;
                    addActive(options.eq(currentFocus));
                    dataList.scrollTop(dataList.scrollTop() + options.eq(currentFocus).position().top);
                } else if (key == 13 && currentFocus > -1) {
                    e.preventDefault();
                    dataList.children('[value]').eq(currentFocus).click();
                }
            });
        }

        handleInput(collegeInput, collegeDataList, collegeCurrentFocus);
        handleInput(degreeInput, degreeDataList, degreeCurrentFocus);

        function addActive(option) {
            option.closest(".spDataList").find(".active").removeClass("active");
            option.addClass("active");
        }
    });
});